import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Star, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { createReview, getTrainerReviews, getMyReviewForTrainer } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { getInitials } from '../lib/utils';
import { toast } from 'sonner';

const formatRelative = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    const diffSec = Math.floor((Date.now() - d.getTime()) / 1000);
    if (diffSec < 60) return 'just now';
    const m = Math.floor(diffSec / 60);
    if (m < 60) return `${m}m`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h`;
    const days = Math.floor(h / 24);
    if (days < 7) return `${days}d`;
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  } catch {
    return iso;
  }
};

const StarRow = ({ value = 0, size = 'sm', interactive = false, onChange }) => {
  const [hover, setHover] = useState(0);
  const sz = size === 'lg' ? 'w-7 h-7' : size === 'md' ? 'w-5 h-5' : 'w-4 h-4';
  return (
    <div className={`flex items-center ${interactive ? 'gap-1.5' : 'gap-0.5'}`}>
      {[1, 2, 3, 4, 5].map((n) => {
        const filled = interactive ? (hover || value) >= n : value >= n;
        return interactive ? (
          <button
            key={n}
            type="button"
            onMouseEnter={() => setHover(n)}
            onMouseLeave={() => setHover(0)}
            onClick={() => onChange?.(n)}
            className="text-[#C0A062] hover:scale-110 transition-transform"
            data-testid={`review-star-${n}`}
            aria-label={`Rate ${n} star${n > 1 ? 's' : ''}`}
          >
            <Star className={`${sz} ${filled ? 'fill-[#C0A062]' : 'fill-transparent'} text-[#C0A062]`} />
          </button>
        ) : (
          <Star
            key={n}
            className={`${sz} ${filled ? 'fill-[#C0A062] text-[#C0A062]' : 'fill-transparent text-zinc-600'}`}
          />
        );
      })}
    </div>
  );
};

const ReviewCard = ({ review, highlighted = false }) => (
  <article
    className={`p-5 rounded-lg border transition-colors ${
      highlighted ? 'bg-[#1C1C20] border-[#C0A062]/40' : 'bg-[#18181B] border-zinc-800 hover:border-zinc-700'
    }`}
    data-testid="review-card"
  >
    <div className="flex items-start gap-3">
      <Avatar className="w-10 h-10 border border-zinc-700 shrink-0">
        <AvatarImage src={review.client_profile_image} alt={review.client_name} />
        <AvatarFallback className="bg-[#27272A] text-[#C0A062] text-xs font-semibold">
          {getInitials(review.client_name || 'A')}
        </AvatarFallback>
      </Avatar>
      <div className="flex-1 min-w-0">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
          <span className="font-semibold text-white text-sm truncate">{review.client_name || 'Anonymous'}</span>
          {highlighted && (
            <span className="text-[10px] uppercase tracking-wider font-bold text-[#C0A062] bg-[#C0A062]/10 px-2 py-0.5 rounded-full">
              Your review
            </span>
          )}
          <span className="text-zinc-500 text-xs">· {formatRelative(review.created_at)}</span>
        </div>
        <div className="mt-1.5">
          <StarRow value={review.rating} />
        </div>
        {review.comment && (
          <p className="mt-2.5 text-zinc-200 text-sm leading-relaxed whitespace-pre-line">{review.comment}</p>
        )}
      </div>
    </div>
  </article>
);

const ReviewsSection = ({ trainerId, trainerName }) => {
  const { user, isAuthenticated, isClient } = useAuth();
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [myReview, setMyReview] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [draftRating, setDraftRating] = useState(0);
  const [draftComment, setDraftComment] = useState('');

  const reload = useCallback(async () => {
    if (!trainerId) return;
    setLoading(true);
    try {
      const [allRes, mineRes] = await Promise.all([
        getTrainerReviews(trainerId),
        isAuthenticated && isClient ? getMyReviewForTrainer(trainerId) : Promise.resolve({ data: { review: null } }),
      ]);
      setReviews(allRes.data || []);
      setMyReview(mineRes.data?.review || null);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [trainerId, isAuthenticated, isClient]);

  useEffect(() => {
    reload();
  }, [reload]);

  const stats = useMemo(() => {
    if (!reviews.length) return { avg: 0, count: 0 };
    const sum = reviews.reduce((s, r) => s + (r.rating || 0), 0);
    return { avg: sum / reviews.length, count: reviews.length };
  }, [reviews]);

  const highlightedReview = myReview ? { ...myReview, client_name: user?.full_name, client_profile_image: user?.client_profile?.profile_image } : null;
  const otherReviews = useMemo(() => {
    if (!myReview) return reviews;
    return reviews.filter((r) => r.id !== myReview.id);
  }, [reviews, myReview]);

  const handleSubmit = async () => {
    if (draftRating < 1) {
      toast.error('Pick a star rating first');
      return;
    }
    setSubmitting(true);
    try {
      const res = await createReview({
        trainer_id: trainerId,
        rating: draftRating,
        comment: draftComment.trim() || null,
      });
      setMyReview(res.data);
      setDraftRating(0);
      setDraftComment('');
      toast.success('Review posted');
      reload();
    } catch (e) {
      console.error(e);
      const msg = e?.response?.data?.detail || 'Could not post review';
      toast.error(typeof msg === 'string' ? msg : 'Could not post review');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-[#18181B] border border-zinc-800 rounded-lg p-6" data-testid="reviews-section">
      <div className="flex items-center justify-between mb-5">
        <h2 className="text-xl font-bold text-white">Reviews</h2>
        {stats.count > 0 && (
          <div className="flex items-center gap-2" data-testid="reviews-summary">
            <StarRow value={Math.round(stats.avg)} size="sm" />
            <span className="text-sm text-white font-semibold">{stats.avg.toFixed(1)}</span>
            <span className="text-sm text-zinc-400">· {stats.count} review{stats.count === 1 ? '' : 's'}</span>
          </div>
        )}
      </div>

      {/* Leave a review form (only for signed-in clients without existing review) */}
      {isAuthenticated && isClient && !myReview && (
        <div className="mb-6 p-4 rounded-lg bg-[#09090B] border border-zinc-800" data-testid="leave-review-form">
          <p className="text-sm text-zinc-300 mb-3">
            Share your experience with {trainerName?.split(' ')[0] || 'this trainer'}.
          </p>
          <div className="mb-3">
            <StarRow value={draftRating} size="md" interactive onChange={setDraftRating} />
          </div>
          <Textarea
            placeholder="Write a quick note (optional)…"
            value={draftComment}
            onChange={(e) => setDraftComment(e.target.value)}
            rows={3}
            className="bg-[#18181B] border-zinc-800 text-white mb-3"
            data-testid="review-comment-input"
          />
          <div className="flex justify-end">
            <Button
              onClick={handleSubmit}
              disabled={submitting}
              className="bg-[#C0A062] text-black hover:bg-[#a98a52] font-semibold"
              data-testid="submit-review-btn"
            >
              {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Post review
            </Button>
          </div>
        </div>
      )}

      {/* Twitter-style timeline */}
      {loading ? (
        <div className="flex items-center justify-center py-12 text-zinc-500">
          <Loader2 className="w-5 h-5 mr-2 animate-spin" />
          Loading reviews…
        </div>
      ) : reviews.length === 0 ? (
        <p className="text-zinc-500 text-sm text-center py-8">No reviews yet. Be the first to leave one.</p>
      ) : (
        <div className="space-y-3" data-testid="reviews-list">
          {highlightedReview && <ReviewCard review={highlightedReview} highlighted />}
          {otherReviews.map((r) => (
            <ReviewCard key={r.id} review={r} />
          ))}
        </div>
      )}
    </div>
  );
};

export default ReviewsSection;
